import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Button, SpinnerLoader } from 'shared/ui'
import { KnowledgeBaseStatus } from 'entities/KnowledgeBase'
import { KnowledgeBasePath } from 'entities/ExportRouters'
import { AgentSwitchField } from 'entities/AIAgent/ui/AgentSwitchField/AgentSwitchField'
import { KnowledgeBaseIndexationStatusAlert } from 'widgets/KnowledgeBase'
import { type AgentKnowledgeBaseStore } from 'pages/chatbot/ui/AgentKnowledgeBase/store/AgentKnowledgeBaseStore'
import { LiveDocumentAccessLostAlert } from 'pages/chatbot/ui/LiveDocumentAccessLostAlert/LiveDocumentAccessLostAlert'
import { KnowledgeBaseAlert } from './KnowledgeBaseAlert/KnowledgeBaseAlert'
import {
  type IKnowledgeBaseDocumentsSelectProps,
  KnowledgeBaseDocumentsSelect,
} from './DocumentsSelect/KnowledgeBaseDocumentsSelect'
import styles from './styles.module.scss'

export type IPointKnowledgeBaseUsageProps = {
  title?: string
  knowledgeBaseDocumentsError?: {
    message: string
    resetError?: () => void
  }
  knowledgeBaseUsageError?: {
    message: string
    resetError?: () => void
  }
  agentKnowledgeBaseStore: AgentKnowledgeBaseStore
  knowledgeBaseUsage: boolean
  setKnowledgeBaseUsage: (value: boolean) => void
  isShowGoToKnowledgeBase?: boolean
  isShowToggle?: boolean
} & Omit<IKnowledgeBaseDocumentsSelectProps, 'agentDocumentsStore'>

export const PointKnowledgeBaseUsage = observer(
  ({
    title = 'Use knowledge base?',
    knowledgeBaseDocumentsError,
    knowledgeBaseUsageError,
    agentKnowledgeBaseStore,
    knowledgeBaseUsage,
    setKnowledgeBaseUsage,
    knowledgeBaseDocuments,
    setKnowledgeBaseDocuments,
    errorType,
    isShowGoToKnowledgeBase,
    isShowToggle = true,
  }: IPointKnowledgeBaseUsageProps) => {
    const { knowledgeBase, isKnowledgeBaseInitLoading, agentDocumentsStore } =
      agentKnowledgeBaseStore
    const {
      isKnowledgeBaseDocumentsEmpty,
      hasGoogleSheetLiveDocument,
      hasGoogleDocLiveDocument,
      hasAccessLostDocument,
    } = agentDocumentsStore
    const handleKnowledgeBaseUsageChange = (knowledgeBaseUsage: boolean) => {
      setKnowledgeBaseUsage(knowledgeBaseUsage)
      knowledgeBaseUsageError?.resetError?.()
    }
    const isKnowledgeBaseEmpty =
      knowledgeBase?.documentsCount === 0 && knowledgeBase.status !== KnowledgeBaseStatus.Processing

    useEffect(() => {
      if (knowledgeBaseUsage && knowledgeBase?.isActiveStatus) {
        knowledgeBaseUsageError?.resetError?.()
      }
      if (!knowledgeBaseUsage) {
        knowledgeBaseDocumentsError?.resetError?.()
      }
    }, [knowledgeBaseUsage, knowledgeBase?.isActiveStatus])

    useEffect(() => {
      if (
        knowledgeBaseDocumentsError?.message.includes(
          'Your AI knowledge base is still processing'
        ) &&
        knowledgeBase?.documentsCount !== 0 &&
        knowledgeBase?.status !== KnowledgeBaseStatus.Processing
      ) {
        knowledgeBaseDocumentsError?.resetError?.()
      }
    }, [knowledgeBaseDocumentsError?.message, knowledgeBase?.status, knowledgeBase?.documentsCount])

    if (isKnowledgeBaseInitLoading) return <SpinnerLoader ariaLabel={'PointKnowledgeBaseUsage'} />

    return (
      <div className={styles.knowledgeBaseContainer}>
        {isShowToggle && (
          <AgentSwitchField
            active={knowledgeBaseUsage}
            onChange={handleKnowledgeBaseUsageChange}
            isBeta
            title={title}
            questionInfoTooltipProps={{
              label: (
                <>
                  Add your business info from AI knowledge base <br /> for more accurate responses
                  and easier setup
                  {isShowGoToKnowledgeBase && (
                    <>
                      <br />
                      <Button
                        typeBtn='link'
                        contained='secondary'
                        text='Go to knowledge base'
                        onClick={() => {
                          window.open(`/${KnowledgeBasePath.root}`, '_blank')
                        }}
                        className={styles.knowledgeBaseLinkButton}
                      />
                    </>
                  )}
                </>
              ),
            }}
          />
        )}

        {knowledgeBaseUsage && (
          <>
            {knowledgeBase && !knowledgeBase.isInitStatus && (
              <KnowledgeBaseIndexationStatusAlert
                knowledgeBase={knowledgeBase}
                isErrorMessage={!!knowledgeBaseDocumentsError?.message}
              />
            )}
            <LiveDocumentAccessLostAlert
              hasGoogleSheetLiveDocument={hasGoogleSheetLiveDocument}
              hasGoogleDocLiveDocument={hasGoogleDocLiveDocument}
              hasAccessLostDocument={hasAccessLostDocument}
            />
            <KnowledgeBaseAlert
              isEmpty={isKnowledgeBaseEmpty}
              error={knowledgeBaseUsageError?.message}
              isKnowledgeBaseDocumentsEmpty={isKnowledgeBaseDocumentsEmpty}
              isBaseErrorMessage={!!knowledgeBaseDocumentsError?.message}
            />
            {knowledgeBase && knowledgeBase.isActiveStatus && !isKnowledgeBaseDocumentsEmpty && (
              <KnowledgeBaseDocumentsSelect
                error={knowledgeBaseDocumentsError}
                agentDocumentsStore={agentDocumentsStore}
                knowledgeBaseDocuments={knowledgeBaseDocuments}
                setKnowledgeBaseDocuments={setKnowledgeBaseDocuments}
                errorType={errorType}
              />
            )}
          </>
        )}
      </div>
    )
  }
)
